import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { IIncidentAnswer, IIncidentObjectEntry, IIncidentObjectField, IIncidentQuestion, IIncidentQuestionOption, IIncidentReport, IIncidentSection } from 'src/app/models/incident.model';
import { IAttachment } from 'src/app/models/attachment.model';
import { faDownload, faExclamationTriangle, faLock, faPlus, faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';

/**
 * Configuração de regiões disponíveis para uma pergunta do tipo "seletor de regiões".
 */
export interface IRegionSelectorConfig {
  /** Regiões faciais disponíveis */
  facial: string[];
  /** Regiões corporais disponíveis */
  corporal: string[];
  /** Regiões pré-selecionadas (ex: vindas dos produtos do plano) */
  preSelected: string[];
}

@Component({
  selector: 'app-incident-form',
  templateUrl: './incident-form.component.html'
})
export class IncidentFormComponent implements OnInit, OnChanges, OnDestroy {
  /** Seções com perguntas vindas da API (ordenadas) */
  @Input() sections: IIncidentSection[] = [];

  /** Respostas existentes para pré-popular o formulário (edição) */
  @Input() existingAnswers: IIncidentAnswer[] = [];

  /** Status do plano de tratamento — se CLOSED, formulário fica read-only */
  @Input() treatmentStatus: 'ACTIVE' | 'CLOSED' = 'ACTIVE';

  /** Status do relatório de intercorrência — se CLOSED, bloqueia edição */
  @Input() incidentStatus: 'ACTIVE' | 'CLOSED' | 'DELETED' = 'ACTIVE';

  /** Indica se o formulário está em modo de salvamento */
  @Input() saving = false;

  /** Indica se está em processo de finalização */
  @Input() finalizing = false;

  /** ID do relatório de intercorrência — vazio enquanto não salvo */
  @Input() incidentId: string = '';

  /** Anexos existentes (edição) */
  @Input() existingAttachments: IAttachment[] = [];

  /** ID do plano de tratamento (para anexos) */
  @Input() treatmentPlanId: string = '';

  /**
   * Valores fixos para perguntas específicas (preenchidos automaticamente e não editáveis).
   * Chave = label normalizado (lowercase, sem acentos) da pergunta.
   * Valor = string a ser preenchida no campo.
   * Ex: { 'data do procedimento': '2026-04-07' }
   */
  @Input() fixedValues: Record<string, string> = {};

  /**
   * Opções dinâmicas para perguntas específicas.
   * Chave = label normalizado da pergunta (ex: 'procedimento da intercorrencia').
   * Valor = array de strings que serão convertidos em opções.
   * As opções existentes da pergunta são substituídas pelas opções dinâmicas.
   */
  @Input() dynamicOptions: Record<string, string[]> = {};

  /**
   * Configuração de perguntas que devem ser renderizadas como seletor de regiões.
   * Chave = label da pergunta (ex: 'Área tratada').
   * Valor = configuração com regiões faciais, corporais e pré-selecionadas.
   */
  @Input() regionSelectorConfig: Record<string, IRegionSelectorConfig> = {};

  /**
   * Quando true, os botões internos de Salvar/Finalizar são ocultados.
   * O componente pai assume o controle (header próprio).
   */
  @Input() hideActions = false;

  /** Emite o payload de respostas quando o usuário submete */
  @Output() formSubmit = new EventEmitter<{ answers: IIncidentAnswer[]; attachments: IAttachment[] }>();

  /**
   * Emite (debounced) sempre que o formulário muda — inclui alterações de campo,
   * regiões e anexos. Usado pelo pai para auto-save silencioso.
   */
  @Output() formChange = new EventEmitter<{ answers: IIncidentAnswer[]; attachments: IAttachment[] }>();

  /** Emite quando o usuário cancela */
  @Output() formCancel = new EventEmitter<void>();

  /** Emite quando o usuário confirma finalizar a intercorrência */
  @Output() formFinalize = new EventEmitter<void>();

  /** Emite quando o usuário solicita reenviar o e-mail da Autentique */
  @Output() resendAutentiqueEmail = new EventEmitter<void>();

  /** Emite quando o usuário clica em Baixar PDF da intercorrência */
  @Output() downloadPdf = new EventEmitter<void>();

  // Ícones
  faSave = faSave;
  faTrash = faTrash;
  faExclamationTriangle = faExclamationTriangle;
  faPlus = faPlus;
  faTimes = faTimes;
  faLock = faLock;
  faDownload = faDownload;

  /** FormGroup raiz — cada chave é o question.id */
  form!: FormGroup;

  /** Seções ordenadas para renderização */
  sortedSections: IIncidentSection[] = [];

  /** Controla visibilidade dos campos de observação por question_id */
  observationVisibility: Record<string, boolean> = {};

  /** IDs dos campos que foram fixados (preenchidos automaticamente e desabilitados) */
  private fixedControlIds: Set<string> = new Set();

  /** Verifica se uma pergunta tem valor fixo (para uso no template) */
  isFixedQuestion(questionId: string): boolean {
    return this.fixedControlIds.has(questionId);
  }

  /** Flag de formulário já submetido (para exibir erros) */
  submitted = false;

  /** Anexos atuais do formulário */
  currentAttachments: IAttachment[] = [];

  /** Controla visibilidade do popup de confirmação de finalização */
  showFinalizeConfirm = false;

  /** Verifica se o formulário está em modo somente leitura */
  get isReadOnly(): boolean {
    return this.treatmentStatus === 'CLOSED' || this.incidentStatus === 'CLOSED';
  }

  // ─── Estado do seletor de regiões ──────────────────────────────────

  /** IDs das perguntas que são seletores de regiões */
  regionSelectorQuestionIds: Set<string> = new Set();

  /** Regiões selecionadas por question_id */
  selectedRegions: Record<string, string[]> = {};

  /** Modal de seleção de regiões aberto por question_id */
  regionModalOpen: Record<string, boolean> = {};

  /** Filtro de busca de regiões por question_id */
  regionSearchFilter: Record<string, string> = {};

  /** Input de região personalizada por question_id */
  customRegionInput: Record<string, string> = {};

  /** Stream interno usado para debounce do auto-save */
  private changeStream$ = new Subject<void>();
  private changeSubscription?: Subscription;
  private valueChangesSubscription?: Subscription;

  constructor(private fb: FormBuilder) {
    // Debounce 1.2s — alinhado com o requestSave do plano de tratamento.
    this.changeSubscription = this.changeStream$
      .pipe(debounceTime(1200))
      .subscribe(() => this.emitFormChange());
  }

  ngOnInit(): void {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sections'] || changes['existingAnswers'] || changes['fixedValues'] || changes['dynamicOptions'] || changes['regionSelectorConfig']) {
      this.buildForm();
    }
    if (changes['treatmentStatus'] && this.form) {
      this.applyReadOnly();
    }
    if (changes['incidentStatus'] && this.form) {
      this.applyReadOnly();
    }
  }

  ngOnDestroy(): void {
    this.changeSubscription?.unsubscribe();
    this.valueChangesSubscription?.unsubscribe();
  }

  /** Notifica mudança no formulário — agenda auto-save com debounce. */
  private notifyChange(): void {
    if (this.isReadOnly) return;
    this.changeStream$.next();
  }

  /** Coleta payload atual do formulário. Pode ser chamado externamente pelo pai. */
  getPayload(): { answers: IIncidentAnswer[]; attachments: IAttachment[] } {
    const answers: IIncidentAnswer[] = [];
    for (const section of this.sortedSections) {
      for (const question of section.questions) {
        this.collectQuestionAnswers(question, answers);
      }
    }
    return { answers, attachments: this.currentAttachments };
  }

  /**
   * Validação de campos obrigatórios para Finalizar.
   * Marca campos como touched, ativa flag de submitted, faz scroll para o primeiro inválido.
   * Retorna `true` se válido (pode finalizar), `false` se há campos faltando.
   */
  validateForFinalize(): boolean {
    if (this.form.invalid) {
      this.submitted = true;
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        control?.markAsTouched();
        if (control instanceof FormArray) {
          control.controls.forEach(c => c.markAsTouched());
        }
      });
      setTimeout(() => {
        const firstInvalid = document.querySelector('.ng-invalid[formControlName], .ng-invalid[formArrayName]') as HTMLElement;
        if (firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstInvalid.focus?.();
        }
      }, 50);
      return false;
    }
    return true;
  }

  /** Emite o estado atual via formChange (sem debounce — uso direto). */
  private emitFormChange(): void {
    if (this.isReadOnly) return;
    this.formChange.emit(this.getPayload());
  }

  // ─── Construção do formulário ───────────────────────────────────────

  /**
   * Normaliza um label para comparação (lowercase, sem acentos, sem espaços extras).
   */
  private normalizeLabel(label: string): string {
    return (label || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  }

  /**
   * Retorna o valor fixo para uma pergunta (se existir), comparando pelo label normalizado.
   */
  private getFixedValue(questionText: string): string | undefined {
    const normalized = this.normalizeLabel(questionText);
    for (const [key, value] of Object.entries(this.fixedValues)) {
      if (this.normalizeLabel(key) === normalized) {
        return value;
      }
    }
    return undefined;
  }

  /**
   * Retorna opções dinâmicas para uma pergunta (se existirem), comparando pelo label normalizado.
   * Converte strings simples em IIncidentQuestionOption com IDs sintéticos.
   */
  private getDynamicOptions(questionText: string): IIncidentQuestionOption[] | undefined {
    const normalized = this.normalizeLabel(questionText);
    for (const [key, values] of Object.entries(this.dynamicOptions)) {
      if (this.normalizeLabel(key) === normalized && values.length > 0) {
        return values.map((text, idx) => ({
          id: text.trim(),
          option_text: text.trim(),
          option_order: idx + 1,
          triggers_free_text: false
        }));
      }
    }
    return undefined;
  }

  /**
   * Retorna a configuração de seletor de regiões para uma pergunta (se existir).
   */
  private getRegionSelectorConfig(questionText: string): IRegionSelectorConfig | undefined {
    const normalized = this.normalizeLabel(questionText);
    for (const [key, config] of Object.entries(this.regionSelectorConfig)) {
      if (this.normalizeLabel(key) === normalized) {
        return config;
      }
    }
    return undefined;
  }

  /**
   * Verifica se uma pergunta deve ser renderizada como seletor de regiões.
   */
  isRegionSelectorQuestion(questionId: string): boolean {
    return this.regionSelectorQuestionIds.has(questionId);
  }

  /**
   * Aplica opções dinâmicas em uma pergunta se houver configuração correspondente.
   * Retorna a pergunta modificada (ou original se não houver opções dinâmicas).
   */
  private applyDynamicOptions(question: IIncidentQuestion): IIncidentQuestion {
    const dynOptions = this.getDynamicOptions(question.question_text);
    if (dynOptions) {
      return { ...question, options: dynOptions };
    }
    return question;
  }

  private buildForm(): void {
    // Limpar o tracking de campos fixos
    this.fixedControlIds = new Set();
    this.regionSelectorQuestionIds = new Set();

    // Inicializar anexos existentes
    this.currentAttachments = [...this.existingAttachments];

    // Ordenar seções e perguntas, aplicando opções dinâmicas quando configuradas
    this.sortedSections = [...this.sections]
      .sort((a, b) => a.section_order - b.section_order)
      .map(section => ({
        ...section,
        questions: [...section.questions]
          .sort((a, b) => a.order_in_section - b.order_in_section)
          .map(q => this.applyDynamicOptions(q))
      }));

    const group: Record<string, FormControl | FormArray> = {};

    for (const section of this.sortedSections) {
      for (const question of section.questions) {
        this.registerQuestionControls(question, group);
      }
    }

    this.form = new FormGroup(group);
    this.applyReadOnly();

    // Subscreve mudanças do FormGroup para auto-save (debounced).
    this.valueChangesSubscription?.unsubscribe();
    this.valueChangesSubscription = this.form.valueChanges.subscribe(() => this.notifyChange());
  }

  /**
   * Registra os controles de formulário para uma pergunta e suas sub-perguntas (recursivo).
   */
  private registerQuestionControls(
    question: IIncidentQuestion,
    group: Record<string, FormControl | FormArray>
  ): void {
    const existingAnswer = this.existingAnswers.find(a => a.question_id === question.id);

    // Verificar se esta pergunta tem valor fixo
    const fixedValue = this.getFixedValue(question.question_text);

    // Verificar se esta pergunta deve ser renderizada como seletor de regiões
    const regionConfig = this.getRegionSelectorConfig(question.question_text);
    if (regionConfig) {
      this.regionSelectorQuestionIds.add(question.id);
      // Inicializar regiões selecionadas a partir da resposta existente ou pré-selecionadas
      const existingValue = existingAnswer?.answer || existingAnswer?.answer_text || '';
      if (existingValue) {
        this.selectedRegions[question.id] = existingValue.split(',').map(s => s.trim()).filter(s => s.length > 0);
      } else if (regionConfig.preSelected.length > 0) {
        this.selectedRegions[question.id] = [...regionConfig.preSelected];
      } else {
        this.selectedRegions[question.id] = [];
      }
      // Form control armazena as regiões como string separada por vírgula
      const regionValue = this.selectedRegions[question.id].join(', ');
      group[question.id] = new FormControl(regionValue, question.is_required ? Validators.required : []);
      // Inicializar estado do modal
      this.regionModalOpen[question.id] = false;
      this.regionSearchFilter[question.id] = '';
      this.customRegionInput[question.id] = '';

      // Observation control
      const obsKey = question.id + '_observation';
      group[obsKey] = new FormControl(existingAnswer?.observation || '');
      this.observationVisibility[question.id] = false;

      // Registrar sub-perguntas se houver
      if (question.options) {
        for (const option of question.options) {
          if (option.sub_questions && option.sub_questions.length > 0) {
            for (const subQ of option.sub_questions) {
              this.registerQuestionControls(subQ, group);
            }
          }
        }
      }
      return;
    }

    switch (question.type) {
      case 'TEXT_INPUT':
      case 'DATE':
      case 'TIME': {
        const value = fixedValue !== undefined ? fixedValue : (existingAnswer?.answer || existingAnswer?.answer_text || '');
        group[question.id] = new FormControl(value, question.is_required ? Validators.required : []);
        if (fixedValue !== undefined) {
          this.fixedControlIds.add(question.id);
        }
        break;
      }

      case 'YES_NO': {
        const value = fixedValue !== undefined ? fixedValue : (existingAnswer?.answer || existingAnswer?.answer_text || '');
        group[question.id] = new FormControl(value, question.is_required ? Validators.required : []);
        if (fixedValue !== undefined) {
          this.fixedControlIds.add(question.id);
        }
        break;
      }

      case 'SINGLE_CHOICE': {
        const value = fixedValue !== undefined ? fixedValue : (existingAnswer?.answer || existingAnswer?.selected_option_ids?.[0] || '');
        group[question.id] = new FormControl(value, question.is_required ? Validators.required : []);
        if (fixedValue !== undefined) {
          this.fixedControlIds.add(question.id);
        }
        break;
      }

      case 'MULTIPLE_CHOICE': {
        // answer pode ser IDs separados por ||| (novo formato), vírgula+maiúscula (legado) ou selected_option_ids array
        const options = this.getSortedOptions(question);
        let selectedIds: Set<string>;
        if (existingAnswer?.answer) {
          let parts: string[];
          if (existingAnswer.answer.includes('|||')) {
            // Novo formato: separador seguro |||
            parts = existingAnswer.answer.split('|||');
          } else {
            // Legado: separar por , seguido de letra maiúscula (evita quebrar nomes com vírgula interna)
            parts = existingAnswer.answer.split(/,\s*(?=[A-ZÀ-ÚÇ])/);
          }
          selectedIds = new Set(parts.map(s => s.trim()).filter(s => s));
        } else {
          selectedIds = new Set(existingAnswer?.selected_option_ids || []);
        }
        console.log(`[MULTIPLE_CHOICE] q=${question.id} existingAnswer="${existingAnswer?.answer}" selectedIds=`, [...selectedIds], 'optionIds=', options.map(o => o.id));

        // Normalizar para comparação case-insensitive como fallback
        const normalizedSelectedIds = new Set([...selectedIds].map(id => id.toLowerCase().replace(/\s+/g, ' ')));

        const controls = options.map(opt => {
          const exactMatch = selectedIds.has(opt.id);
          const normalizedMatch = !exactMatch && normalizedSelectedIds.has(opt.id.toLowerCase().replace(/\s+/g, ' '));
          if (normalizedMatch) {
            console.warn(`[MULTIPLE_CHOICE] Fallback match for option "${opt.id}" (normalized comparison)`);
          }
          return new FormControl(exactMatch || normalizedMatch);
        });
        const arr = new FormArray(controls);
        // Validação custom: ao menos um selecionado se required
        if (question.is_required) {
          arr.setValidators(() => {
            const hasSelected = arr.controls.some(c => c.value === true);
            return hasSelected ? null : { required: true };
          });
        }
        group[question.id] = arr;
        break;
      }

      case 'OBJECT': {
        // FormArray de FormGroups — cada entry é um FormGroup com campos dinâmicos
        const fields = this.getSortedObjectFields(question);
        const existingEntries = existingAnswer?.object_entries || [];
        const entryGroups = existingEntries.length > 0
          ? existingEntries.map(entry => this.createObjectEntryGroup(fields, entry))
          : [];
        const entriesArr = new FormArray(entryGroups);
        if (question.is_required) {
          entriesArr.setValidators(() => {
            return entriesArr.length > 0 ? null : { required: true };
          });
        }
        group[question.id] = entriesArr;
        break;
      }
    }

    // Observation (free text) control para cada pergunta
    const obsKey = question.id + '_observation';
    const obsValue = existingAnswer?.observation || '';
    group[obsKey] = new FormControl(obsValue);

    // Determinar visibilidade inicial da observação
    this.observationVisibility[question.id] = this.shouldShowObservation(question, existingAnswer);

    // Registrar controles para sub-perguntas de todas as opções
    if (question.options) {
      for (const option of question.options) {
        if (option.sub_questions && option.sub_questions.length > 0) {
          for (const subQ of option.sub_questions) {
            this.registerQuestionControls(subQ, group);
          }
        }
      }
    }
  }

  private applyReadOnly(): void {
    if (!this.form) return;
    if (this.isReadOnly) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  // ─── Lógica de triggers_free_text ──────────────────────────────────

  /** Verifica se a observação deve estar visível com base nas opções selecionadas */
  private shouldShowObservation(question: IIncidentQuestion, answer?: IIncidentAnswer): boolean {
    if (question.type === 'SINGLE_CHOICE') {
      const selectedId = answer?.answer || answer?.selected_option_ids?.[0];
      if (!selectedId) return false;
      const option = question.options.find(o => o.id === selectedId);
      return option?.triggers_free_text === true;
    }
    if (question.type === 'MULTIPLE_CHOICE') {
      let selectedIds: Set<string>;
      if (answer?.answer) {
        const parts = answer.answer.includes('|||')
          ? answer.answer.split('|||')
          : answer.answer.split(/,\s*(?=[A-ZÀ-ÚÇ])/);
        selectedIds = new Set(parts.map(s => s.trim()).filter(s => s));
      } else {
        selectedIds = new Set(answer?.selected_option_ids || []);
      }
      return question.options.some(o => selectedIds.has(o.id) && o.triggers_free_text);
    }
    return false;
  }

  /** Chamado quando uma opção de SINGLE_CHOICE muda */
  onSingleChoiceChange(question: IIncidentQuestion, selectedOptionId: string): void {
    const option = question.options.find(o => o.id === selectedOptionId);
    this.observationVisibility[question.id] = option?.triggers_free_text === true;
    if (!this.observationVisibility[question.id]) {
      this.form.get(question.id + '_observation')?.setValue('');
    }
  }

  /** Verifica se uma opção específica está selecionada (para mostrar sub-perguntas) */
  isOptionSelected(question: IIncidentQuestion, option: IIncidentQuestionOption, optionIndex?: number): boolean {
    if (question.type === 'SINGLE_CHOICE') {
      return this.form.get(question.id)?.value === option.id;
    }
    if (question.type === 'MULTIPLE_CHOICE' && optionIndex !== undefined) {
      const formArray = this.form.get(question.id) as FormArray;
      return formArray?.at(optionIndex)?.value === true;
    }
    return false;
  }

  /** Chamado quando um checkbox de MULTIPLE_CHOICE muda */
  onMultipleChoiceChange(question: IIncidentQuestion): void {
    const sortedOptions = this.getSortedOptions(question);
    const formArray = this.form.get(question.id) as FormArray;
    const anyTrigger = sortedOptions.some((opt, i) => formArray.at(i).value === true && opt.triggers_free_text);
    this.observationVisibility[question.id] = anyTrigger;
    if (!anyTrigger) {
      this.form.get(question.id + '_observation')?.setValue('');
    }
  }

  // ─── Helpers para template ─────────────────────────────────────────

  /** Retorna opções ordenadas de uma pergunta.
   *  Opções com triggers_free_text ficam sempre no final da lista. */
  getSortedOptions(question: IIncidentQuestion) {
    return [...question.options].sort((a, b) => {
      // Opções com triggers_free_text vão para o final
      if (a.triggers_free_text !== b.triggers_free_text) {
        return a.triggers_free_text ? 1 : -1;
      }
      return a.option_order - b.option_order;
    });
  }

  /** Retorna o FormArray de MULTIPLE_CHOICE */
  getMultipleChoiceArray(questionId: string): FormArray {
    return this.form.get(questionId) as FormArray;
  }

  /**
   * Para SINGLE_CHOICE: verifica se a opção específica está selecionada E tem triggers_free_text.
   */
  isOptionTriggeringObservation(question: IIncidentQuestion, option: IIncidentQuestionOption): boolean {
    if (question.type === 'SINGLE_CHOICE') {
      const selectedId = this.form.get(question.id)?.value;
      return selectedId === option.id && option.triggers_free_text === true;
    }
    return false;
  }

  /**
   * Para MULTIPLE_CHOICE: verifica se o checkbox de uma opção com triggers_free_text está marcado.
   */
  isMultipleOptionTriggeringObservation(question: IIncidentQuestion, option: IIncidentQuestionOption, index: number): boolean {
    if (!option.triggers_free_text) return false;
    const formArray = this.form.get(question.id) as FormArray;
    return formArray?.at(index)?.value === true;
  }

  /** Verifica se um campo tem erro e foi submetido ou tocado */
  hasError(questionId: string): boolean {
    const control = this.form.get(questionId);
    if (!control) return false;
    return control.invalid && (this.submitted || control.touched);
  }

  // ─── Helpers de exibição read-only ─────────────────────────────────

  /** Valor de exibição de uma pergunta TEXT_INPUT/DATE/TIME (com formatação leve). */
  getReadOnlyValue(question: IIncidentQuestion): string {
    const raw = this.form.get(question.id)?.value;
    if (raw === null || raw === undefined || raw === '') return '—';

    if (question.type === 'DATE') {
      // YYYY-MM-DD → DD/MM/YYYY
      const m = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[3]}/${m[2]}/${m[1]}`;
      return String(raw);
    }
    if (question.type === 'TIME') {
      // HH:mm já vem formatado
      return String(raw);
    }
    return String(raw);
  }

  /** Valor de exibição para YES_NO. */
  getReadOnlyYesNoValue(questionId: string): string {
    const v = this.form.get(questionId)?.value;
    if (v === 'SIM') return 'Sim';
    if (v === 'NAO') return 'Não';
    return '—';
  }

  /** Texto da opção selecionada em SINGLE_CHOICE. */
  getReadOnlySingleChoiceText(question: IIncidentQuestion): string {
    const selectedId = this.form.get(question.id)?.value;
    if (!selectedId) return '—';
    const option = (question.options || []).find(o => o.id === selectedId);
    return option?.option_text || String(selectedId);
  }

  /** Lista de opções marcadas em MULTIPLE_CHOICE. */
  getReadOnlyMultipleChoiceItems(question: IIncidentQuestion): string[] {
    const formArray = this.form.get(question.id) as FormArray;
    if (!formArray) return [];
    const sortedOptions = this.getSortedOptions(question);
    return sortedOptions
      .filter((_, i) => formArray.at(i)?.value === true)
      .map(opt => opt.option_text);
  }

  /** Valor da observação livre (campo {questionId}_observation). */
  getReadOnlyObservation(questionId: string): string {
    const v = this.form.get(questionId + '_observation')?.value;
    return (v && String(v).trim()) ? String(v) : '';
  }

  // ─── OBJECT helpers ────────────────────────────────────────────────

  /** Retorna os campos do objeto ordenados por field_order */
  getSortedObjectFields(question: IIncidentQuestion): IIncidentObjectField[] {
    return [...(question.object_fields || [])].sort((a, b) => a.field_order - b.field_order);
  }

  /** Cria um FormGroup para uma entrada de objeto (um "item" — ex: um medicamento) */
  createObjectEntryGroup(fields: IIncidentObjectField[], existingEntry?: IIncidentObjectEntry): FormGroup {
    const group: Record<string, FormControl> = {};
    for (const field of fields) {
      const existingValue = existingEntry?.values?.find(v => v.field_key === field.field_key)?.field_value || '';
      group[field.field_key] = new FormControl(existingValue, field.is_required ? Validators.required : []);
    }
    return new FormGroup(group);
  }

  /** Retorna o FormArray de entradas de objeto de uma pergunta */
  getObjectEntriesArray(questionId: string): FormArray {
    return this.form.get(questionId) as FormArray;
  }

  /** Adiciona uma nova entrada (item) vazia ao objeto */
  addObjectEntry(question: IIncidentQuestion): void {
    const fields = this.getSortedObjectFields(question);
    const arr = this.getObjectEntriesArray(question.id);
    arr.push(this.createObjectEntryGroup(fields));
  }

  /** Remove uma entrada de objeto pelo índice */
  removeObjectEntry(questionId: string, index: number): void {
    this.getObjectEntriesArray(questionId).removeAt(index);
  }

  /** Retorna o tipo de input HTML para um field_type de objeto */
  getObjectFieldInputType(fieldType: string): string {
    switch (fieldType) {
      case 'NUMBER': return 'number';
      case 'TIME': return 'time';
      case 'DATE': return 'date';
      default: return 'text';
    }
  }

  // ─── REGION SELECTOR helpers ───────────────────────────────────────

  /** Retorna a config de regiões para uma pergunta (pelo question_id) */
  getRegionConfig(questionId: string): IRegionSelectorConfig | null {
    // Procurar pela pergunta para obter o label
    for (const section of this.sortedSections) {
      for (const question of section.questions) {
        if (question.id === questionId) {
          return this.getRegionSelectorConfig(question.question_text) || null;
        }
      }
    }
    return null;
  }

  /** Abre o modal de seleção de regiões */
  openRegionModal(questionId: string): void {
    if (this.isReadOnly) return;
    this.regionModalOpen[questionId] = true;
    this.regionSearchFilter[questionId] = '';
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }
  }

  /** Fecha o modal de seleção de regiões */
  closeRegionModal(questionId: string): void {
    this.regionModalOpen[questionId] = false;
    this.regionSearchFilter[questionId] = '';
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }

  /** Lista de regiões customizadas selecionadas (não estão em facial nem corporal) */
  getCustomRegionsList(questionId: string): string[] {
    const config = this.getRegionConfig(questionId);
    const selected = this.selectedRegions[questionId] || [];
    if (!config) return selected;
    const standard = new Set([...(config.facial || []), ...(config.corporal || [])]);
    return selected.filter(r => !standard.has(r));
  }

  /** Retorna regiões faciais filtradas */
  getFilteredFacialRegions(questionId: string): string[] {
    const config = this.getRegionConfig(questionId);
    if (!config) return [];
    const filter = (this.regionSearchFilter[questionId] || '').toLowerCase().trim();
    if (!filter) return config.facial;
    return config.facial.filter(r => r.toLowerCase().includes(filter));
  }

  /** Retorna regiões corporais filtradas */
  getFilteredCorporalRegions(questionId: string): string[] {
    const config = this.getRegionConfig(questionId);
    if (!config) return [];
    const filter = (this.regionSearchFilter[questionId] || '').toLowerCase().trim();
    if (!filter) return config.corporal;
    return config.corporal.filter(r => r.toLowerCase().includes(filter));
  }

  /** Verifica se uma região está selecionada */
  isRegionSelected(questionId: string, regionName: string): boolean {
    return (this.selectedRegions[questionId] || []).includes(regionName);
  }

  /** Toggle de uma região (adicionar ou remover) */
  toggleRegion(questionId: string, regionName: string): void {
    if (this.isReadOnly) return;
    const regions = this.selectedRegions[questionId] || [];
    const idx = regions.indexOf(regionName);
    if (idx >= 0) {
      regions.splice(idx, 1);
    } else {
      regions.push(regionName);
    }
    this.selectedRegions[questionId] = [...regions];
    this.syncRegionFormControl(questionId);
  }

  /** Remove uma região pelo nome */
  removeRegion(questionId: string, regionName: string): void {
    if (this.isReadOnly) return;
    const regions = this.selectedRegions[questionId] || [];
    const idx = regions.indexOf(regionName);
    if (idx >= 0) {
      regions.splice(idx, 1);
      this.selectedRegions[questionId] = [...regions];
      this.syncRegionFormControl(questionId);
    }
  }

  /** Adiciona região personalizada */
  addCustomRegion(questionId: string): void {
    if (this.isReadOnly) return;
    const value = (this.customRegionInput[questionId] || '').trim();
    if (!value) return;
    const regions = this.selectedRegions[questionId] || [];
    if (!regions.includes(value)) {
      regions.push(value);
      this.selectedRegions[questionId] = [...regions];
      this.syncRegionFormControl(questionId);
    }
    this.customRegionInput[questionId] = '';
  }

  /** Callback do input de filtro de regiões */
  onRegionSearchChange(questionId: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.regionSearchFilter[questionId] = input.value;
  }

  /** Sincroniza as regiões selecionadas com o FormControl */
  private syncRegionFormControl(questionId: string): void {
    const regions = this.selectedRegions[questionId] || [];
    const value = regions.join(', ');
    // setValue dispara valueChanges → notifyChange já é chamado.
    this.form.get(questionId)?.setValue(value);
    this.form.get(questionId)?.markAsTouched();
  }

  /**
   * Atualiza as regiões pré-selecionadas para uma pergunta de seletor de regiões.
   * Chamado externamente quando o procedimento selecionado muda.
   */
  updatePreSelectedRegions(questionLabel: string, regions: string[]): void {
    const normalized = this.normalizeLabel(questionLabel);
    for (const section of this.sortedSections) {
      for (const question of section.questions) {
        if (this.regionSelectorQuestionIds.has(question.id) && this.normalizeLabel(question.question_text) === normalized) {
          this.selectedRegions[question.id] = [...regions];
          this.syncRegionFormControl(question.id);
          break;
        }
      }
    }
  }

  // ─── Submit ────────────────────────────────────────────────────────

  /**
   * Save manual: emite o payload sem validar campos obrigatórios.
   * Mantém o comportamento de rascunho — validação de required só no Finalize.
   */
  onSubmit(): void {
    this.formSubmit.emit(this.getPayload());
  }

  /**
   * Coleta respostas de uma pergunta e suas sub-perguntas recursivamente.
   * Sub-perguntas são coletadas como respostas flat no array (mesmo formato de resposta normal).
   */
  private collectQuestionAnswers(question: IIncidentQuestion, answers: IIncidentAnswer[]): void {
    const answer: IIncidentAnswer = { question_id: question.id };

    switch (question.type) {
      case 'TEXT_INPUT':
      case 'YES_NO':
      case 'DATE':
      case 'TIME':
        answer.answer = this.form.get(question.id)?.value || '';
        break;

      case 'SINGLE_CHOICE': {
        const selectedId = this.form.get(question.id)?.value;
        answer.answer = selectedId || '';
        break;
      }

      case 'MULTIPLE_CHOICE': {
        const formArray = this.getMultipleChoiceArray(question.id);
        const sortedOptions = this.getSortedOptions(question);
        const selectedIds = sortedOptions
          .filter((_, i) => formArray.at(i).value === true)
          .map(opt => opt.id);
        // Usar ||| como separador para evitar conflito com vírgulas nos nomes dos procedimentos
        answer.answer = selectedIds.join('|||');
        break;
      }

      case 'OBJECT': {
        const entriesArr = this.getObjectEntriesArray(question.id);
        const fields = this.getSortedObjectFields(question);
        answer.object_entries = entriesArr.controls.map((entryCtrl, idx) => {
          const entryGroup = entryCtrl as FormGroup;
          return {
            entry_order: idx + 1,
            values: fields.map(field => ({
              field_key: field.field_key,
              field_label: field.field_label,
              field_value: entryGroup.get(field.field_key)?.value || ''
            }))
          };
        });
        break;
      }
    }

    // Observation
    if (this.observationVisibility[question.id]) {
      answer.observation = this.form.get(question.id + '_observation')?.value || '';
    }

    answers.push(answer);

    // Coletar respostas de sub-perguntas das opções selecionadas
    if (question.options) {
      const sortedOptions = this.getSortedOptions(question);
      sortedOptions.forEach((opt, idx) => {
        if (opt.sub_questions && opt.sub_questions.length > 0 && this.isOptionSelected(question, opt, idx)) {
          for (const subQ of opt.sub_questions) {
            this.collectQuestionAnswers(subQ, answers);
          }
        }
      });
    }
  }

  /** Callback do componente de anexos */
  onAttachmentsChange(attachments: IAttachment[]): void {
    this.currentAttachments = attachments;
    this.notifyChange();
  }

  onCancel(): void {
    this.formCancel.emit();
  }

  /** Abre o popup de confirmação de finalização — exige todos os campos obrigatórios preenchidos */
  openFinalizeConfirm(): void {
    if (this.form.invalid) {
      this.submitted = true;

      // Marcar todos os campos como touched para mostrar erros
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        control?.markAsTouched();
        if (control instanceof FormArray) {
          control.controls.forEach(c => c.markAsTouched());
        }
      });

      // Scroll até o primeiro campo com erro
      setTimeout(() => {
        const firstInvalid = document.querySelector('.ng-invalid[formControlName], .ng-invalid[formArrayName]') as HTMLElement;
        if (firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstInvalid.focus?.();
        }
      }, 50);

      return;
    }

    this.showFinalizeConfirm = true;
  }

  /** Fecha o popup de confirmação de finalização */
  closeFinalizeConfirm(): void {
    this.showFinalizeConfirm = false;
  }

  /** Confirma a finalização — emite evento para o componente pai gerar o PDF e enviar */
  confirmFinalize(): void {
    this.showFinalizeConfirm = false;
    this.formFinalize.emit();
  }

  /** Handler para reenviar o e-mail da Autentique */
  onResendAutentiqueEmail(): void {
    this.resendAutentiqueEmail.emit();
  }

  /** Handler para baixar o PDF da intercorrência */
  onDownloadPdf(): void {
    this.downloadPdf.emit();
  }
}
